import React, { useState, useCallback, useContext, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import ContractContext from '../../ContractContext';
import { useWeb3React } from "@web3-react/core";
import { Message } from 'primereact/message';
import { Card } from 'primereact/card'; // Import Card
import ierc20Abi from '../../contracts/ierc20Abi';
import Web3 from 'web3';
import { formatDecimals, formatWalletAddress, formatCurrencyExtended, formatBalanceWithIcon, updateToken} from '../../utils';


const AddReward = ({getData}) => {

    const initialDexOptions = [
        { name: "Pancake Swap", address: "0x10ED43C718714eb63d5aA57B78B54704E256024E" },
        { name: "Baby Swap", address: "0x325E343f1dE602396E256B67eFd1F61C3A6B38Bd" },
        { name: "BabyDoge Swap", address: "0xC9a0F685F39d05D835c369036251ee3aEaaF3c47" },
        { name: "Ape Swap", address: "0xcF0feBd3f17CEf5b47b0cD257aCf6025c5BFf3b7" },
        { name: "BiSwap", address: "0x3a6d8cA21D1CF76F653A67577FA0D27453350dD8" },
        { name: "Bakery Swap", address: "0xCDe540d7eAFE93aC5fE6233Bee57E1270D3E330F" },
        { name: "InterLink Swap", address: "0x12f3E7e3281ebEf98e098F861A6d29920a47142F" },
        // Add other initial DEX options here...
    ];

    const renderSteps = () => {
        return (
            <Card title="Steps" style={{ marginBottom: '2em' }}>
                <>
                    <p>1: Paste any BSC token address.</p>
                    <p>2: Select a Dex Pair.</p>
                    <p>3: Confirm the Transaction.</p>
                    <p style={{color: 'gray'}}>
                        <strong>Note:</strong> A 24-hour cooldown is in place between adding new rewards.
                    </p>
                    <p style={{color: 'gray'}}>
                        <strong>Requirement:</strong> You must hold a balance to add rewards.
                    </p>
                </>
            </Card>
        );
    };
    

    const { contracts } = useContext(ContractContext);
    const { account } = useWeb3React();
    const [tokenAddress, setTokenAddress] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [dexOptions, setDexOptions] = useState([...initialDexOptions]);
    const [canAddReward, setCanAddReward] = useState(true);
    const toast = useRef(null);

    const WBNB_ADDRESS = "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c";

    const isWalletConnected = () => account !== null;

    const [tokenDetails, setTokenDetails] = useState({
        name: '',
        symbol: '',
        totalSupply: '',
        decimals: '',
        balance: '',
    });


    const isValidAddress = (address) => {
        return address.startsWith('0x') && address.length === 42;
    };

    const fetchTokenDetails = useCallback(async (tokenAddress) => {
        if (!tokenAddress || !account) return;

        const web3 = new Web3(window.ethereum);
        const ierc20 = new web3.eth.Contract(ierc20Abi, tokenAddress);

        try {
            const [name, symbol, totalSupply, decimals] = await Promise.all([
                ierc20.methods.name().call(),
                ierc20.methods.symbol().call(),
                ierc20.methods.totalSupply().call(),
                ierc20.methods.decimals().call(),
            ]);
            const balance = await ierc20.methods.balanceOf(account).call();

            setTokenDetails({
                name,
                symbol,
                totalSupply,
                decimals,
                balance,
            });
        } catch (error) {
            console.error("Error fetching token details:", error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to fetch token details.', life: 5000 });
        }
    }, [account]);

    useEffect(() => {
        if (showModal) {
            fetchTokenDetails(tokenAddress);
        }
    }, [showModal, tokenAddress, fetchTokenDetails]);

    useEffect(() => {
        //console.log('token Details:', tokenDetails);
    }, [tokenDetails]);

    const checkCooldown = useCallback(async () => {
        if (!account || !contracts.rp_rewardPackManager) {
            return;
        }
        const canAdd = await contracts.rp_rewardPackManager.methods.canAddReward(account).call();
        setCanAddReward(canAdd);
    }, [account, contracts.rp_rewardPackManager]);

    useEffect(() => {
        checkCooldown();
    }, [checkCooldown]);

    const filterValidDexOptions = async () => {
        if (!isWalletConnected() || !contracts.rp_rewardPackManager || !tokenAddress) {
            return;
        }

        setIsLoading(true);
        // Reset dexOptions to initial state before running new simulations
        setDexOptions([...initialDexOptions]);

        let validDexOptions = [];
        const minAmountWei = await contracts.rp_rewardPackManager.methods.getTestMinAmount().call();

        // Map through initialDexOptions and simulate each one
        const simulations = initialDexOptions.map(async (dex) => {
            try {
                await contracts.rp_rewardPackManager.methods.addReward(tokenAddress, dex.address).estimateGas({ from: account, value: minAmountWei });
                return dex; // Return dex if simulation is successful
            } catch (error) {
                console.error(`${dex.name} simulation failed: ${error.message}`);
                return null; // Return null if simulation fails
            }
        });

        // Wait for all simulations to complete
        const results = await Promise.all(simulations);
        validDexOptions = results.filter(dex => dex !== null); // Filter out nulls

        if (validDexOptions.length > 0) {
            setDexOptions(validDexOptions); // Update state with only valid options
            setShowModal(true); // Show modal if there are valid options
        } else {
            // No valid DEX options found, inform the user and clear the token address
            setDexOptions([]);
            toast.current.show({
                severity: 'warn',
                summary: 'No Valid DEX Options',
                detail: 'No DEX options are currently available for adding a reward without reverting. Please try another token address.',
                life: 5000
            });
            setTokenAddress(""); // Clear the token address field
        }

        setIsLoading(false);
    };




    const checkTokenAndPool = useCallback(async () => {
        if (!isWalletConnected()) {
            toast.current.show({ severity: 'error', summary: 'Wallet Error', detail: 'Please connect to a wallet.', life: 3000 });
            return;
        }

        if (!contracts.rp_rewardPacks) {
            toast.current.show({ severity: 'error', summary: 'Contract Error', detail: 'Smart Rewards contract not available.', life: 3000 });
            return;
        }

        try {
            const result = await contracts.rp_rewardPacks.methods.reflectionPoolAtERC20(tokenAddress).call();
            const poolID = result.index;
            const validPoolId = poolID !== '0' || tokenAddress === WBNB_ADDRESS;

            if (validPoolId) {
                toast.current.show({ severity: 'success', summary: 'Token Exists', detail: 'Token exists! Please select it in smart rewards.', life: 3000 });
            } else {
                setShowModal(true); // No valid pool ID found, show modal to add reward
            }
            return validPoolId;
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to check token.', life: 3000 });

            return false;
        }
    }, [tokenAddress, contracts.rp_rewardPacks, account]);

    useEffect(() => {
        if (tokenAddress) {
            checkTokenAndPool().then((validPoolId) => {
                // Call filterValidDexOptions only if token doesn't have a valid pool ID
                if (!validPoolId) filterValidDexOptions();
            });
        }
    }, [tokenAddress, checkTokenAndPool]);


    const handleTokenAddressChange = (e) => {
        setTokenAddress(e.target.value);
    };

    const handleModalClose = () => {
        setShowModal(false);
        setTokenAddress("");
        setTokenDetails({ name: '', symbol: '', totalSupply: '' });
    };

    // const simulateAddReward = async (dexAddress) => {
    //     if (!isWalletConnected() || !contracts.rp_rewardPackManager) {
    //         console.error('Wallet not connected or contract not loaded');
    //         return false;
    //     }

    //     try {
    //         const minAmountWei = await contracts.rp_rewardPackManager.methods.getTestMinAmount().call();
    //         // Estimate Gas
    //         const gasEstimate = await contracts.rp_rewardPackManager.methods.addReward(tokenAddress, dexAddress).estimateGas({ from: account, value: minAmountWei });

    //         // Call to simulate (this part assumes addReward is not a view or pure function and doesn't return a useful value for call)
    //         await contracts.rp_rewardPackManager.methods.addReward(tokenAddress, dexAddress).call({ from: account, value: minAmountWei });

    //         //console.log(`Simulation successful, estimated gas: ${gasEstimate}`);
    //         return true; // Simulation didn't throw, indicating success
    //     } catch (error) {
    //        // console.error(`Simulation failed: ${error.message}`);
    //         return false; // Simulation failed, indicating transaction would likely revert
    //     }
    // };

    // const handleSelectDex = async (dexAddress) => {
    //     setIsLoading(true);
    //     // const simulationSuccess = await simulateAddReward(dexAddress);
    //     // if (!simulationSuccess) {
    //     //     toast.current.show({ severity: 'error', summary: 'Error', detail: 'Unable To Add Token.', life: 5000 });
    //     //     setIsLoading(false);
    //     //     return;
    //     // }

    //     // if (simulationSuccess) {
    //       try {
    //         const minAmountWei = await contracts.rp_rewardPackManager.methods.getTestMinAmount().call();
    //         // Proceed with actual send if simulation succeeds
    //         const txReceipt = await contracts.rp_rewardPackManager.methods.addReward(tokenAddress, dexAddress).send({ from: account, value: minAmountWei });

    //         txReceipt.on("transactionHash", (hash) => {
    //             toast.current.show({ severity: 'info', summary: 'Transaction Submitted', detail: `Transaction Hash: ${hash}. Follow the transaction on BscScan here: https://bscscan.com/tx/${hash}`, life: 3000 });
    //           });


    //         const tx = await txReceipt;

    //         toast.current.show({ severity: 'success', summary: 'Transaction Success', detail: 'The transaction has been completed successfully.', life: 3000 });
    //         //call updateToken async function to update token details

    //         await updateToken(tokenAddress);
            
    //         handleModalClose(); // Close modal after successful transaction
    //       } catch (error) {
    //         // Display error toast message
    //         toast.current.show({ severity: 'error', summary: 'Error', detail: 'Transaction failed.', life: 5000 });
    //         //console.error(`Transaction failed: ${error.message}`);
    //       }
    //     // } else {
    //     //   // Display error toast message if simulation fails
    //     //   toast.current.show({ severity: 'error', summary: 'Error', detail: 'Simulation failed.', life: 5000 });
    //     // }
    //     setIsLoading(false);
    //     getData();
    //   };

    const handleSelectDex = async (dexAddress) => {
        setIsLoading(true);
    
        try {
            const minAmountWei = await contracts.rp_rewardPackManager.methods.getTestMinAmount().call();
            
            // Using `await` with `.send` to ensure we wait for the transaction to complete.
            await contracts.rp_rewardPackManager.methods.addReward(tokenAddress, dexAddress).send({ from: account, value: minAmountWei })
                .on('transactionHash', (hash) => {
                    toast.current.show({ severity: 'info', summary: 'Transaction Submitted', detail: `Transaction Hash: ${hash}. Follow the transaction on BscScan here: https://bscscan.com/tx/${hash}`, life: 3000 });
                })
                .on('receipt', (receipt) => {
                    // This callback is called when the transaction is confirmed
                    toast.current.show({ severity: 'success', summary: 'Transaction Success', detail: 'The transaction has been completed successfully.', life: 3000 });
                    
                    // Ensure data refresh is called here, after transaction success
                    getData();
                });
            
            // Optional: Close modal here if you prefer it to close only after successful transaction
            handleModalClose();
        } catch (error) {
            // This will catch any errors that occur during the transaction sending
            toast.current.show({ severity: 'error', summary: 'Transaction Failed', detail: 'The transaction has failed. ' + error.message, life: 5000 });
            console.error('Transaction failed:', error);
            getData();
        }
    
        setIsLoading(false);
        // Moved getData() into the 'receipt' event listener for success path
    };
    


    return (
        <div style={{
            marginTop: '25px',
        }}>
            <Toast ref={toast} />
            <div>
                {renderSteps()}
                <InputText
                    id="tokenAddress"
                    value={tokenAddress}
                    onChange={handleTokenAddressChange}
                    placeholder={!canAddReward ? "❄️ Cannot add rewards due to cooldown. ❄️" : "Enter token address"}
                    disabled={!canAddReward}
                    style={{ width: '100%' }}
                />

            </div>
            <Dialog visible={showModal} onHide={handleModalClose} header="Token Information & Select DEX" modal>
                {isLoading ? (
                    <div 
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '10px'
                    }}
                >
                    <ProgressSpinner
                        style={{ width: '200px', justifyContent: 'center', }}
                    />
                </div>
                ) : (
                    <>
                        <div>
                        <strong>Name:</strong> {tokenDetails.name}<br />
                            <strong>Symbol:</strong> {tokenDetails.symbol}<br />
                            <strong>Total Supply:</strong> {formatDecimals(tokenDetails.totalSupply, tokenDetails.decimals)}<br />
                            <strong>Decimals:</strong> {tokenDetails.decimals}<br />
                            <strong>Your Balance:</strong> {formatDecimals(tokenDetails.balance, tokenDetails.decimals)}<br />
                        </div>
                        <div className="p-grid">
                            {dexOptions.map((dex, index) => (
                                <div key={index}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        padding: '10px'
                                    }}
                                >
                                    <Button label={dex.name} onClick={() => {handleSelectDex(dex.address);} }
                                        style={{ width: '200px', justifyContent: 'center', }}
                                    />
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </Dialog>

        </div>
    );
};

export default AddReward;


